import { useCallback } from 'react';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Panel from '@/features/report/shared/Panel/Panel';
import type { FollowUpGroup, FollowUpLabel } from '@/models/template';
import followUpStyles from '@/features/report/FollowUp/FollowUpPanel.module.css';
import sectionStyles from '@/features/report/Findings/FindingGroupSection/FindingGroupSection.module.css';
import { EditableText } from '../primitives/EditableText';
import { RowControls } from '../primitives/RowControls';
import { AddButton } from '../primitives/AddButton';
import { ClassPicker } from '../primitives/ClassPicker';
import { useFollowUpClassOptions } from '../editorOptions';
import styles from './EditableFollowUpSection.module.css';

// Keyed by the label uuid when there is one, so a chip keeps its identity
// (and its in-progress edit) when an earlier chip is deleted.
const optionDragId = (groupUuid: string, option: FollowUpLabel, idx: number) =>
  `${groupUuid}::${option.uuid ?? idx}`;

interface EditableFollowUpSectionProps {
  groups: FollowUpGroup[];
  title?: string;
  onChange: (groups: FollowUpGroup[]) => void;
}

export function EditableFollowUpSection({
  groups,
  title = 'Follow Up',
  onChange,
}: EditableFollowUpSectionProps) {
  const handleGroupChange = useCallback(
    (idx: number, next: FollowUpGroup) => {
      const copy = [...groups];
      copy[idx] = next;
      onChange(copy);
    },
    [groups, onChange]
  );

  return (
    <Panel title={title}>
      <div className={styles.container}>
        {groups.map((group, idx) => (
          <EditableFollowUpGroup
            key={group.uuid}
            group={group}
            position={idx + 1}
            onChange={(next) => handleGroupChange(idx, next)}
          />
        ))}
      </div>
    </Panel>
  );
}

interface EditableFollowUpGroupProps {
  group: FollowUpGroup;
  position: number;
  onChange: (group: FollowUpGroup) => void;
}

function EditableFollowUpGroup({
  group,
  position,
  onChange,
}: EditableFollowUpGroupProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const followUpClasses = useFollowUpClassOptions();
  const usedClasses = new Set(
    group.followUpLabels.map((l) => l.followUpClass).filter(Boolean)
  );

  const handleOptionChange = useCallback(
    (idx: number, next: FollowUpLabel) => {
      const copy = [...group.followUpLabels];
      copy[idx] = next;
      onChange({ ...group, followUpLabels: copy });
    },
    [group, onChange]
  );

  const handleOptionDelete = useCallback(
    (idx: number) => {
      onChange({
        ...group,
        followUpLabels: group.followUpLabels.filter((_, i) => i !== idx),
      });
    },
    [group, onChange]
  );

  const handleAddOption = useCallback(() => {
    const maxIndex = group.followUpLabels.reduce(
      (m, l) => Math.max(m, l.index ?? 0),
      -1
    );
    const newOption: FollowUpLabel = {
      uuid: crypto.randomUUID(),
      followUpClass: '',
      label: '',
      index: maxIndex + 1,
    };
    onChange({
      ...group,
      followUpLabels: [...group.followUpLabels, newOption],
    });
  }, [group, onChange]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const activeId = String(active.id);
      const overId = String(over.id);
      const oldIdx = group.followUpLabels.findIndex(
        (l, i) => optionDragId(group.uuid, l, i) === activeId
      );
      const newIdx = group.followUpLabels.findIndex(
        (l, i) => optionDragId(group.uuid, l, i) === overId
      );
      if (oldIdx === -1 || newIdx === -1) return;
      const reordered = arrayMove(group.followUpLabels, oldIdx, newIdx).map(
        (l, i) => ({ ...l, index: i })
      );
      onChange({ ...group, followUpLabels: reordered });
    },
    [group, onChange]
  );

  const itemIds = group.followUpLabels.map((l, idx) =>
    optionDragId(group.uuid, l, idx)
  );

  return (
    <div className={styles.group}>
      <div className={sectionStyles.header}>
        <span className={sectionStyles.accentBar} />
        <span className={sectionStyles.title}>
          {position}.{' '}
          <EditableText
            value={group.label}
            onChange={(label) => onChange({ ...group, label })}
            placeholder="Group name"
          />
        </span>
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={itemIds}
          strategy={horizontalListSortingStrategy}
        >
          <div className={followUpStyles.row}>
            {group.followUpLabels.map((opt, idx) => (
              <SortableFollowUpChip
                key={optionDragId(group.uuid, opt, idx)}
                id={optionDragId(group.uuid, opt, idx)}
                option={opt}
                classOptions={followUpClasses}
                usedClasses={usedClasses}
                onChange={(next) => handleOptionChange(idx, next)}
                onDelete={() => handleOptionDelete(idx)}
              />
            ))}
            <AddButton onClick={handleAddOption} label="Add option" />
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

interface SortableFollowUpChipProps {
  id: string;
  option: FollowUpLabel;
  /** Follow-up classes the PDF matches on. Empty in the worksheet editor. */
  classOptions: string[];
  usedClasses: Set<string>;
  onChange: (next: FollowUpLabel) => void;
  onDelete: () => void;
}

function SortableFollowUpChip({
  id,
  option,
  classOptions,
  usedClasses,
  onChange,
  onDelete,
}: SortableFollowUpChipProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <span
      ref={setNodeRef}
      style={style}
      className={`${followUpStyles.chip} ${styles.draggableChip}`}
      data-editable-row
      {...attributes}
      {...listeners}
    >
      <span className={followUpStyles.chipLabel}>
        <EditableText
          value={option.label}
          onChange={(value) => onChange({ ...option, label: value })}
          placeholder="Option"
        />
      </span>
      {classOptions.length > 0 && (
        <ClassPicker
          value={option.followUpClass}
          options={classOptions}
          usedClasses={usedClasses}
          onChange={(followUpClass) => onChange({ ...option, followUpClass })}
        />
      )}
      <RowControls onDelete={onDelete} />
    </span>
  );
}
